import { useEffect, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { ChevronDown, ChevronRight, Image as ImageIcon, Loader2, Plus, Trash2 } from 'lucide-react';
import { qualityAPI, ApprovalGroup, Accreditation, fetchAllPages } from '../../../lib/api';
import { toast } from 'sonner';
import { useConfirm } from '../../../contexts/ConfirmDialogContext';

// CR-5333: Admin → Quality → Approvals Management.
// Mirrors the Fault Groups & Categories section in QualityTab — the
// pattern was already validated for the same two-tier shape, so the
// only differences here are domain wording (groups → accreditations)
// and the API endpoints we hit.

export default function ApprovalsManagementSection() {
  const confirm = useConfirm();
  const [groups, setGroups] = useState<ApprovalGroup[]>([]);
  const [accreditations, setAccreditations] = useState<Accreditation[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState<number | null>(null);

  const [newGroupName, setNewGroupName] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);

  const [newAccName, setNewAccName] = useState('');
  const [addingAcc, setAddingAcc] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      // Walk pages — the backend clamps page_size, so one request silently truncated.
      const [allGroups, allAccs] = await Promise.all([
        fetchAllPages<ApprovalGroup>((page) => qualityAPI.getApprovalGroups({ page_size: 100, page })),
        fetchAllPages<Accreditation>((page) => qualityAPI.getAccreditations({ page_size: 100, page })),
      ]);
      setGroups(allGroups);
      setAccreditations(allAccs);
    } catch (err) {
      console.error('Failed to load approval groups:', err);
      toast.error('Failed to load approval groups');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      setAddingGroup(true);
      const resp = await qualityAPI.createApprovalGroup({ name, is_active: true });
      setGroups(prev => [...prev, resp.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewGroupName('');
    } catch (err) {
      console.error('Failed to add approval group:', err);
      toast.error('Failed to add approval group');
    } finally {
      setAddingGroup(false);
    }
  };

  const handleDeleteGroup = async (id: number, name: string) => {
    const ok = await confirm({
      title: 'Delete approval group',
      description: `Delete "${name}" and all of its accreditations?`,
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await qualityAPI.deleteApprovalGroup(id);
      setGroups(prev => prev.filter(g => g.id !== id));
      setAccreditations(prev => prev.filter(a => a.approval_group !== id));
      if (expandedGroup === id) setExpandedGroup(null);
    } catch (err) {
      console.error('Failed to delete approval group:', err);
      toast.error('Failed to delete approval group');
    }
  };

  const handleAddAccreditation = async (groupId: number) => {
    const name = newAccName.trim();
    if (!name) return;
    try {
      setAddingAcc(true);
      const resp = await qualityAPI.createAccreditation({
        name,
        approval_group: groupId,
        is_active: true,
      });
      setAccreditations(prev => [...prev, resp.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewAccName('');
    } catch (err) {
      console.error('Failed to add accreditation:', err);
      toast.error('Failed to add accreditation');
    } finally {
      setAddingAcc(false);
    }
  };

  // CR-5534: upload (or replace) the brand logo on an accreditation so it
  // renders in the Approval Register matrix column header.
  const fileInputs = useRef<Record<number, HTMLInputElement | null>>({});
  const [uploadingLogo, setUploadingLogo] = useState<number | null>(null);

  const handleLogoSelected = async (id: number, file: File | null | undefined) => {
    if (!file) return;
    try {
      setUploadingLogo(id);
      const resp = await qualityAPI.uploadAccreditationLogo(id, file);
      setAccreditations(prev => prev.map(a => (a.id === id ? resp.data : a)));
      toast.success('Logo updated');
    } catch (err) {
      console.error('Failed to upload logo:', err);
      toast.error('Failed to upload logo');
    } finally {
      setUploadingLogo(null);
    }
  };

  const handleDeleteAccreditation = async (id: number, name: string) => {
    const ok = await confirm({
      title: 'Delete accreditation',
      description: `Delete "${name}"?`,
      confirmLabel: 'Delete',
      variant: 'destructive',
    });
    if (!ok) return;
    try {
      await qualityAPI.deleteAccreditation(id);
      setAccreditations(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error('Failed to delete accreditation:', err);
      toast.error('Failed to delete accreditation');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Approvals Management</CardTitle>
        <p className="text-sm text-muted-foreground">
          Define grouped accreditation requirements (e.g. Aerospace) that suppliers must confirm they hold.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…
          </div>
        ) : (
          <>
            {groups.map(group => {
              const groupAccs = accreditations.filter(a => a.approval_group === group.id);
              const isExpanded = expandedGroup === group.id;

              return (
                <div key={group.id} className="border rounded-md">
                  <div
                    className="flex items-center justify-between py-2 px-3 cursor-pointer hover:bg-muted/50"
                    onClick={() => setExpandedGroup(isExpanded ? null : group.id)}
                  >
                    <div className="flex items-center gap-2">
                      {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                      <span className="text-sm font-medium">{group.name}</span>
                      <span className="text-xs text-muted-foreground">
                        ({groupAccs.length} {groupAccs.length === 1 ? 'accreditation' : 'accreditations'})
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={(e) => { e.stopPropagation(); handleDeleteGroup(group.id, group.name); }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>

                  {isExpanded && (
                    <div className="border-t px-6 py-3 space-y-2 bg-muted/30">
                      {groupAccs.map(acc => (
                        <div key={acc.id} className="flex items-center justify-between py-1.5 px-3 bg-background rounded-md">
                          <div className="flex items-center gap-3">
                            {acc.logo_url ? (
                              <img
                                src={acc.logo_url}
                                alt={acc.name}
                                className="h-8 w-12 object-contain"
                              />
                            ) : (
                              <div className="h-8 w-12 flex items-center justify-center text-muted-foreground bg-muted/40 rounded">
                                <ImageIcon className="h-4 w-4" />
                              </div>
                            )}
                            <span className="text-sm">{acc.name}</span>
                          </div>
                          <div className="flex items-center gap-1">
                            <input
                              ref={(el) => { fileInputs.current[acc.id] = el; }}
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => handleLogoSelected(acc.id, e.target.files?.[0])}
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs"
                              disabled={uploadingLogo === acc.id}
                              onClick={() => fileInputs.current[acc.id]?.click()}
                            >
                              {uploadingLogo === acc.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <ImageIcon className="h-3 w-3 mr-1" />
                              )}
                              {acc.logo_url ? 'Change logo' : 'Upload logo'}
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 text-destructive hover:text-destructive"
                              onClick={() => handleDeleteAccreditation(acc.id, acc.name)}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      ))}
                      {groupAccs.length === 0 && (
                        <p className="text-xs text-muted-foreground">No accreditations yet.</p>
                      )}
                      <div className="flex gap-2 pt-1">
                        <Input
                          value={newAccName}
                          onChange={(e) => setNewAccName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') handleAddAccreditation(group.id); }}
                          placeholder="New accreditation name (e.g. AS9100)..."
                          className="flex-1 h-8 text-sm"
                        />
                        <Button
                          onClick={() => handleAddAccreditation(group.id)}
                          disabled={addingAcc || !newAccName.trim()}
                          size="sm"
                          className="h-8"
                        >
                          {addingAcc ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3 mr-1" />}
                          Add
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {groups.length === 0 && (
              <p className="text-sm text-muted-foreground py-2">No approval groups configured yet.</p>
            )}
            <div className="flex gap-2 pt-2">
              <Label className="sr-only">New approval group</Label>
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAddGroup(); }}
                placeholder="New approval group name (e.g. Aerospace)..."
                className="flex-1"
              />
              <Button onClick={handleAddGroup} disabled={addingGroup || !newGroupName.trim()} size="sm">
                {addingGroup ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                Add Group
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
